import { useState, useCallback, useEffect } from 'react';

import { useUnit } from 'effector-react';
import { useForm, type Resolver, type FieldErrors, type FieldError } from 'react-hook-form';

import { Search$ } from '../../../../../effector/search';
import { Position } from '../../../../../types/position';
import { Nullable } from '../../../../../utils/types';
import { FormFields } from '../types';
import { createValidationSchema } from '../validation';

export const useSignalForm = (
  activeTabId: number,
  editablePosition: Nullable<Position>,
  priceRange: { min: number; max: number } | null,
  limitOrderTimeoutMax: number,
) => {
  const searchStore = useUnit(Search$);
  const [signalType, setSignalType] = useState<number>();
  const [weight, setWeight] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  const {
    control,
    watch,
    setValue,
    trigger,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      price: '',
      size: editablePosition
        ? Number(editablePosition.weight) > 0
          ? editablePosition.weight?.toString()
          : (editablePosition.weight * -1).toString()
        : '',
      stoploss: editablePosition ? editablePosition.stopLoss?.toString() ?? '' : '',
      takeprofit: editablePosition ? editablePosition.takeProfit?.toString() ?? '' : '',
      type: editablePosition ? (Number(editablePosition.weight) > 0 ? 1 : 2) : '',
      description: '',
      timeout: '1',
    },
    mode: 'onChange',
    resolver: ((values) => {
      const validationSchema = createValidationSchema(activeTabId, priceRange, limitOrderTimeoutMax);
      const fieldErrors: Partial<Record<keyof FormFields, FieldError>> = {};

      Object.entries(validationSchema).forEach(([field, validate]) => {
        const error = validate(values[field as keyof FormFields] as string, values);
        if (error !== true) {
          const fieldName = field as keyof FormFields;
          fieldErrors[fieldName] = {
            type: 'manual',
            message: error,
          };
        }
      });

      const errors = fieldErrors as FieldErrors<FormFields>;
      const hasErrors = Object.keys(fieldErrors).length > 0;

      return {
        values: hasErrors ? ({} as Record<string, never>) : values,
        errors,
      };
    }) as Resolver<FormFields>,
  });

  const values = watch();

  useEffect(() => {
    const weight = Number(values.size);
    weight ? setWeight(weight) : setWeight(0);
  }, [values.size]);

  useEffect(() => {
    if (!editablePosition) {
      setValue('type', 1);
    }
  }, [searchStore?.details?.key, activeTabId, editablePosition, setValue]);

  useEffect(() => {
    setValue('price', '');
    setValue('timeout', '');

    if (activeTabId === 5 || activeTabId === 2) {
      setValue('timeout', '1');
    }
  }, [activeTabId, setValue]);

  useEffect(() => {
    if (activeTabId === 5) {
      void trigger('timeout');
    }
  }, [activeTabId, limitOrderTimeoutMax, trigger]);

  useEffect(() => {
    setSignalType(values.type as number);
    if (Number(values.type) === 3) {
      setValue('size', '');
      setValue('stoploss', '');
      setValue('takeprofit', '');
    }
  }, [values.type, setValue]);

  useEffect(() => {
    setComment(values.description);
  }, [values.description]);

  const resetForm = useCallback(
    (tabId: number = activeTabId, overrides: Partial<FormFields> = {}) => {
      reset({
        price: '',
        size: '',
        stoploss: '',
        takeprofit: '',
        type: 1,
        description: '',
        timeout: tabId === 2 || tabId === 5 ? '1' : '',
        ...overrides,
      });
    },
    [activeTabId, reset],
  );

  return {
    control,
    values,
    errors,
    signalType,
    weight,
    comment,
    handleSubmit,
    setValue,
    resetForm,
  };
};
